/**
 * Hedefler's data (Faz 23, T-182; w-hedefler.html): which goals the list shows, the numbers
 * under a goal's title, the order of a stage's tasks, and a task's date label. Pure, so the
 * page and its tests share one source.
 */
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { daysBetween } from '../../utils/zonedTime';

export type GoalFilter = 'active' | 'favorites' | 'done' | 'drafts';

/** Goals with an open task carrying [tagId]; all goals when no tag is set. */
const hasTag = (goal: Goal, tagId: string | null): boolean =>
  !tagId || (goal.subGoals ?? []).some((s) => (s.todos ?? []).some((t) => t.tags?.some((tag) => tag.id === tagId)));

/** The goals of a list chip, favourites first, then the latest touched. */
export const filterGoals = (goals: Goal[], filter: GoalFilter, tagId: string | null = null): Goal[] => {
  const pick = goals.filter((g) => {
    if (!hasTag(g, tagId)) return false;
    if (filter === 'done') return g.completed;
    if (filter === 'favorites') return Boolean(g.isFavorite);
    if (filter === 'active') return !g.completed;
    return false;
  });
  const touched = (g: Goal) => g.updatedAt ?? g.createdAt ?? '';
  return pick.sort((a, b) => {
    if (filter === 'active' && Boolean(a.isFavorite) !== Boolean(b.isFavorite)) return a.isFavorite ? -1 : 1;
    return touched(b).localeCompare(touched(a));
  });
};

export interface GoalNumbers {
  done: number;
  total: number;
  stages: number;
  /** Days to the target date (negative when past), or null without one. */
  daysLeft: number | null;
  workedSeconds: number;
  /** Open tasks' estimates, seconds. */
  remainingSeconds: number;
  /** Open tasks due today. */
  today: number;
  /** Open tasks due before today. */
  late: number;
}

/** The goal row's and the detail's stats line. [today] is 'YYYY-MM-DD' in the account's zone. */
export const goalNumbers = (goal: Goal, today: string): GoalNumbers => {
  const n: GoalNumbers = {
    done: 0,
    total: 0,
    stages: (goal.subGoals ?? []).length,
    daysLeft: goal.dueDate ? daysBetween(today, goal.dueDate.slice(0, 10)) : null,
    workedSeconds: 0,
    remainingSeconds: 0,
    today: 0,
    late: 0,
  };
  for (const s of goal.subGoals ?? []) {
    for (const t of s.todos ?? []) {
      n.total += 1;
      n.workedSeconds += t.totalTimeSpent ?? 0;
      if (t.completed) {
        n.done += 1;
        continue;
      }
      n.remainingSeconds += t.estimatedSeconds ?? 0;
      if (t.dueDate === today) n.today += 1;
      else if (t.dueDate && t.dueDate < today) n.late += 1;
    }
  }
  return n;
};

/** A stage's tasks in their saved order (orderIndex, then creation). */
export const orderedTodos = (stage: SubGoal): Todo[] =>
  [...(stage.todos ?? [])].sort(
    (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '')
  );

/** The open tasks first (in order), then the finished ones. */
export const stageRows = (stage: SubGoal): { open: Todo[]; done: Todo[] } => {
  const all = orderedTodos(stage);
  return { open: all.filter((t) => !t.completed), done: all.filter((t) => t.completed) };
};

/** A stage folds when it is finished, or every task in it is. */
export const stageFolded = (stage: SubGoal): boolean => {
  if (stage.completed) return true;
  const todos = stage.todos ?? [];
  return todos.length > 0 && todos.every((t) => t.completed);
};

/** [list] with the item at [from] moved to [to]. */
export const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, item);
  return next;
};

/**
 * The orderIndex payload for a stage after a move: the open tasks in their new order, then
 * the finished ones, numbered from 0.
 */
export const orderPayload = (open: Todo[], done: Todo[]): Array<{ id: string; orderIndex: number }> =>
  [...open, ...done].map((t, i) => ({ id: t.id, orderIndex: i }));

export type DateTone = 'today' | 'late' | 'plain';

/** What the date cell says: today, a weekday in the coming week, else day and month. */
export const dueLabel = (
  due: string | null | undefined,
  today: string,
  language: string,
  todayWord: string
): { text: string; tone: DateTone } | null => {
  if (!due) return null;
  const day = due.slice(0, 10);
  const diff = daysBetween(today, day);
  const date = new Date(`${day}T12:00:00Z`);
  if (diff === 0) return { text: todayWord, tone: 'today' };
  const locale = language || undefined;
  if (diff > 0 && diff < 7) {
    return { text: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date), tone: 'plain' };
  }
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  const text = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(date);
  return { text, tone: diff < 0 ? 'late' : 'plain' };
};

/** The goal (and stage) that hold a stage id; for #/subgoals/:id links. */
export const findStage = (goals: Goal[], stageId: string): { goal: Goal; stage: SubGoal } | null => {
  for (const goal of goals) {
    const stage = (goal.subGoals ?? []).find((s) => s.id === stageId);
    if (stage) return { goal, stage };
  }
  return null;
};

/** Where a hash points inside Hedefler. */
export interface BoardRoute {
  goalId: string | null;
  stageId: string | null;
  tagId: string | null;
  drafts: boolean;
}

export const parseBoardHash = (hash: string): BoardRoute => {
  const path = hash.replace(/^#/, '');
  const [base, query = ''] = path.split('?');
  const tagId = new URLSearchParams(query).get('tag');
  const goal = base.match(/^\/goals\/([^/]+)$/);
  const stage = base.match(/^\/subgoals\/([^/]+)\/todos$/);
  return {
    goalId: goal ? decodeURIComponent(goal[1]) : null,
    stageId: stage ? decodeURIComponent(stage[1]) : null,
    tagId: tagId || null,
    drafts: base === '/ai-drafts',
  };
};
